import { z } from "zod"

export const SIDEBAR_SECTIONS = ["personal", "inbox", "workspace"] as const
export type SidebarSection = (typeof SIDEBAR_SECTIONS)[number]

export const SIDEBAR_VISIBILITIES = ["always", "badged", "hidden"] as const
export type SidebarVisibility = (typeof SIDEBAR_VISIBILITIES)[number]

export const SIDEBAR_BADGE_STYLES = ["count", "dot", "off"] as const
export type SidebarBadgeStyle = (typeof SIDEBAR_BADGE_STYLES)[number]

/** Default order per section. `badgeable` items can carry a count, so they offer "Show when badged". */
export const SIDEBAR_ITEMS = {
	inbox: { section: "personal", badgeable: true },
	notifications: { section: "personal", badgeable: true },
	agent: { section: "personal", badgeable: false },
	// The inbox tabs, as rows of their own in the combined sidebar. The rail never lists them.
	"inbox-new": { section: "inbox", badgeable: true },
	"inbox-sla-breached": { section: "inbox", badgeable: true },
	"inbox-my": { section: "inbox", badgeable: true },
	"inbox-close-loop": { section: "inbox", badgeable: true },
	"inbox-open": { section: "inbox", badgeable: true },
	// The Agent page: what the agent recommends, first in the Workspace group.
	recommendations: { section: "workspace", badgeable: true },
	reporting: { section: "workspace", badgeable: false },
	docs: { section: "workspace", badgeable: true },
	"feature-requests": { section: "workspace", badgeable: true },
	"support-portal": { section: "workspace", badgeable: false },
	changelog: { section: "workspace", badgeable: false },
	customers: { section: "workspace", badgeable: false },
} as const satisfies Record<
	string,
	{ section: SidebarSection; badgeable: boolean }
>

export type SidebarItemId = keyof typeof SIDEBAR_ITEMS

/** Inbox tab value to its sidebar item. */
export const INBOX_TAB_ITEM_IDS = {
	"sla-breached": "inbox-sla-breached",
	new: "inbox-new",
	my: "inbox-my",
	"close-loop": "inbox-close-loop",
	open: "inbox-open",
} as const satisfies Record<string, SidebarItemId>

export type InboxTabItemId =
	(typeof INBOX_TAB_ITEM_IDS)[keyof typeof INBOX_TAB_ITEM_IDS]

export function inboxTabForItem(id: string): string | undefined {
	return Object.entries(INBOX_TAB_ITEM_IDS).find(
		([, itemId]) => itemId === id,
	)?.[0]
}

const ITEM_IDS = Object.keys(SIDEBAR_ITEMS) as SidebarItemId[]

export function isSidebarItemId(value: string): value is SidebarItemId {
	return Object.prototype.hasOwnProperty.call(SIDEBAR_ITEMS, value)
}

const defaultOrder = (section: SidebarSection) =>
	ITEM_IDS.filter((id) => SIDEBAR_ITEMS[id].section === section)

export const COMBINED_SIDEBAR_GROUPS = ["top", "workspace"] as const
export type CombinedSidebarGroup = (typeof COMBINED_SIDEBAR_GROUPS)[number]

/** Rows the combined sidebar keeps above its Workspace group: what is yours to act on. */
export const COMBINED_TOP_IDS: readonly SidebarItemId[] = [
	"agent",
	"notifications",
	"inbox-my",
]

export function combinedGroupOf(id: string): CombinedSidebarGroup {
	return (COMBINED_TOP_IDS as readonly string[]).includes(id)
		? "top"
		: "workspace"
}

/** The combined sidebar's order before anyone reorders it there, read off the section orders. */
function combinedOrderFrom(
	order: Record<SidebarSection, readonly SidebarItemId[]>,
): SidebarItemId[] {
	const isTop = (id: SidebarItemId) => combinedGroupOf(id) === "top"
	// The Assistant leads the combined sidebar whatever the rail's order, so it is 1.
	return [
		"agent",
		...[...order.personal, ...order.inbox].filter(
			(id) => isTop(id) && id !== "agent",
		),
		...order.inbox.filter((id) => !isTop(id)),
		...order.personal.filter((id) => !isTop(id)),
		...order.workspace,
	]
}

export const sidebarLayoutSchema = z.object({
	v: z.literal(1),
	order: z.object({
		personal: z.array(z.string()),
		// Optional: layouts saved before the combined sidebar have no inbox order.
		inbox: z.array(z.string()).optional(),
		workspace: z.array(z.string()),
		// The combined sidebar's own order, one list across both of its groups.
		combined: z.array(z.string()).optional(),
	}),
	visibility: z.record(z.string(), z.enum(SIDEBAR_VISIBILITIES)),
	badgeStyle: z.enum(SIDEBAR_BADGE_STYLES),
})

/** The stored shape on Membership.sidebarLayout. */
export type StoredSidebarLayout = z.infer<typeof sidebarLayoutSchema>

export interface SidebarLayout {
	order: Record<SidebarSection, SidebarItemId[]>
	combined: SidebarItemId[]
	visibility: Record<SidebarItemId, SidebarVisibility>
	badgeStyle: SidebarBadgeStyle
}

const DEFAULT_ORDER: Record<SidebarSection, SidebarItemId[]> = {
	personal: defaultOrder("personal"),
	inbox: defaultOrder("inbox"),
	workspace: defaultOrder("workspace"),
}

const DEFAULT_VISIBILITY: Partial<Record<SidebarItemId, SidebarVisibility>> = {
	"inbox-sla-breached": "badged",
	"inbox-close-loop": "badged",
}

export const DEFAULT_SIDEBAR_LAYOUT: SidebarLayout = {
	order: DEFAULT_ORDER,
	combined: combinedOrderFrom(DEFAULT_ORDER),
	visibility: Object.fromEntries(
		ITEM_IDS.map((id) => [id, DEFAULT_VISIBILITY[id] ?? "always"]),
	) as Record<SidebarItemId, SidebarVisibility>,
	badgeStyle: "count",
}

/**
 * Keeps the saved order for known items and slots items the save never saw
 * (shipped after it) back in after their nearest default predecessor.
 */
function resolveOrder(
	defaults: readonly SidebarItemId[],
	saved: readonly string[] | undefined,
): SidebarItemId[] {
	const order: SidebarItemId[] = []
	for (const id of saved ?? []) {
		if (isSidebarItemId(id) && defaults.includes(id) && !order.includes(id)) {
			order.push(id)
		}
	}
	defaults.forEach((id, index) => {
		if (order.includes(id)) return
		const before = defaults
			.slice(0, index)
			.reverse()
			.find((candidate) => order.includes(candidate))
		order.splice(before ? order.indexOf(before) + 1 : 0, 0, id)
	})
	return order
}

/**
 * Saves from before the Assistant joined the top group list it among the
 * Workspace rows; dropping it there lets it take its default first slot.
 */
function withoutLegacyAgent(
	saved: readonly string[] | undefined,
): readonly string[] | undefined {
	if (!saved) return saved
	const agentAt = saved.indexOf("agent")
	const firstWorkspaceAt = saved.findIndex(
		(id) => isSidebarItemId(id) && combinedGroupOf(id) === "workspace",
	)
	if (agentAt === -1 || firstWorkspaceAt === -1 || agentAt < firstWorkspaceAt)
		return saved
	return saved.filter((id) => id !== "agent")
}

// Older saves interleave Unassigned (once a top row) with the top rows, so an
// Assistant dragged past it would read as a legacy save and snap back to first.
function topGroupFirst(order: readonly SidebarItemId[]): SidebarItemId[] {
	return [
		...order.filter((id) => combinedGroupOf(id) === "top"),
		...order.filter((id) => combinedGroupOf(id) === "workspace"),
	]
}

/** Tolerant read: anything malformed or unknown falls back to the default for that piece. */
export function resolveSidebarLayout(raw: unknown): SidebarLayout {
	const parsed = sidebarLayoutSchema.safeParse(raw)
	if (!parsed.success) return DEFAULT_SIDEBAR_LAYOUT

	const visibility = { ...DEFAULT_SIDEBAR_LAYOUT.visibility }
	for (const [id, value] of Object.entries(parsed.data.visibility)) {
		if (!isSidebarItemId(id)) continue
		visibility[id] =
			value === "badged" && !SIDEBAR_ITEMS[id].badgeable ? "always" : value
	}

	const saved = parsed.data.order
	const order = {
		personal: resolveOrder(defaultOrder("personal"), saved.personal),
		inbox: resolveOrder(defaultOrder("inbox"), saved.inbox),
		workspace: resolveOrder(defaultOrder("workspace"), saved.workspace),
	}
	return {
		order,
		combined: topGroupFirst(
			resolveOrder(
				combinedOrderFrom(order),
				withoutLegacyAgent(saved.combined),
			),
		),
		visibility,
		badgeStyle: parsed.data.badgeStyle,
	}
}

export function toStoredSidebarLayout(
	layout: SidebarLayout,
): StoredSidebarLayout {
	return {
		v: 1,
		order: {
			personal: layout.order.personal,
			inbox: layout.order.inbox,
			workspace: layout.order.workspace,
			combined: layout.combined,
		},
		visibility: layout.visibility,
		badgeStyle: layout.badgeStyle,
	}
}

export function isSidebarItemShown(
	visibility: SidebarVisibility,
	count: number | undefined,
): boolean {
	if (visibility === "always") return true
	if (visibility === "badged") return (count ?? 0) > 0
	return false
}

/** Moves `id` to `targetId`'s slot within its section, like dnd-kit's arrayMove. */
export function moveSidebarItem(
	layout: SidebarLayout,
	id: SidebarItemId,
	targetId: SidebarItemId,
): SidebarLayout {
	const section = SIDEBAR_ITEMS[id].section
	if (SIDEBAR_ITEMS[targetId].section !== section) return layout
	const order = [...layout.order[section]]
	const from = order.indexOf(id)
	const to = order.indexOf(targetId)
	if (from < 0 || to < 0 || from === to) return layout
	order.splice(from, 1)
	order.splice(to, 0, id)
	return { ...layout, order: { ...layout.order, [section]: order } }
}

/** Same move for the combined sidebar, kept within one of its two groups. */
export function moveCombinedSidebarItem(
	layout: SidebarLayout,
	id: SidebarItemId,
	targetId: SidebarItemId,
): SidebarLayout {
	if (combinedGroupOf(id) !== combinedGroupOf(targetId)) return layout
	const order = [...layout.combined]
	const from = order.indexOf(id)
	const to = order.indexOf(targetId)
	if (from < 0 || to < 0 || from === to) return layout
	order.splice(from, 1)
	order.splice(to, 0, id)
	return { ...layout, combined: order }
}

export function setSidebarItemVisibility(
	layout: SidebarLayout,
	id: SidebarItemId,
	visibility: SidebarVisibility,
): SidebarLayout {
	const next =
		visibility === "badged" && !SIDEBAR_ITEMS[id].badgeable
			? "always"
			: visibility
	return { ...layout, visibility: { ...layout.visibility, [id]: next } }
}
